/**
 * EInvoicePanel.js
 * ─────────────────
 * E-Invoice (IRN) sidebar panel for InvoicePreview.
 * Mirrors the EWayBillPanel design pattern exactly.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import fetchApi from '../../utils/api';

// ── Constants ─────────────────────────────────────────────────────────────────
const CANCEL_REASONS = [
    { value: '1', label: 'Duplicate' },
    { value: '2', label: 'Data Entry Mistake' },
    { value: '3', label: 'Order Cancelled' },
    { value: '4', label: 'Others' },
];

const TRANS_MODES = [
    { value: '1', label: 'Road' },
    { value: '2', label: 'Rail' },
    { value: '3', label: 'Air' },
    { value: '4', label: 'Ship' },
];

const STATUS_COLORS = {
    GENERATED: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7', label: 'Generated' },
    CANCELLED: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', label: 'Cancelled' },
    PENDING:   { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', label: 'Pending' },
};

// ── Styles ────────────────────────────────────────────────────────────────────
const panelStyle = {
    width: '300px', minWidth: '280px', background: '#fff',
    borderLeft: '1px solid #e2e8f0', display: 'flex',
    flexDirection: 'column', gap: 0, height: '100%',
    overflowY: 'auto', fontSize: '13px',
    fontFamily: 'Inter, Arial, sans-serif',
};
const sectionStyle   = { borderBottom: '1px solid #e2e8f0', padding: '12px 16px' };
const labelStyle     = { fontWeight: 600, color: '#374151', fontSize: '12px', marginBottom: '2px' };
const inputStyle     = { width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', background: '#f8fafc', outline: 'none', marginTop: '2px' };
const selectStyle    = { ...inputStyle, cursor: 'pointer' };
const btnPrimary     = { width: '100%', padding: '9px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', marginTop: '8px', letterSpacing: '0.3px' };
const btnSecondary   = { width: '100%', padding: '8px', background: '#f1f5f9', color: '#374151', border: '1px solid #cbd5e1', borderRadius: '7px', fontWeight: 500, fontSize: '13px', cursor: 'pointer', marginTop: '6px' };
const btnDanger      = { ...btnSecondary, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' };
const infoRow        = { display: 'flex', marginBottom: '4px' };
const infoLabel      = { fontWeight: 700, color: '#374151', width: '110px', minWidth: '110px', fontSize: '12px' };
const infoVal        = { color: '#1e293b', fontSize: '12px' };

// ═════════════════════════════════════════════════════════════════════════════
export default function EInvoicePanel({ invoiceData, onRefresh, onDownloadPDF }) {
    const invoiceId = invoiceData?._id || invoiceData?.id;

    const einvoiceStatus = invoiceData?.einvoice_status || 'PENDING';
    const irn            = invoiceData?.irn || '';
    const ackNo          = invoiceData?.ack_no || '';
    const ackDate        = invoiceData?.ack_date || '';
    const signedQR       = invoiceData?.signed_qr_code || '';
    const cancelDate     = invoiceData?.irn_cancel_date || '';
    const cancelRemark   = invoiceData?.irn_cancel_remark || '';

    const statusInfo = STATUS_COLORS[einvoiceStatus] || STATUS_COLORS.PENDING;

    // ── State ─────────────────────────────────────────────────────────────────
    const [loading, setLoading]           = useState(false);
    const [confirmed, setConfirmed]       = useState(false);

    // Cancel form
    const [showCancel, setShowCancel]     = useState(false);
    const [cancelForm, setCancelForm]     = useState({ cancelReason: '1', cancelRemark: '' });

    // Generate EWB by IRN form
    const [showEWB, setShowEWB]           = useState(false);
    const [ewbForm, setEwbForm]           = useState({ transMode: '1', vehNo: '', vehType: 'R', transId: '', transName: '', transDocNo: '', transDocDate: '', distance: '0' });

    // API response viewer
    const [showResponse, setShowResponse] = useState(false);
    const [apiResponse, setApiResponse]   = useState(null);

    // ── Generate IRN ──────────────────────────────────────────────────────────
    const handleGenerateIRN = async () => {
        if (!confirmed) { toast.warn('Please confirm before generating E-Invoice / IRN'); return; }
        if (!invoiceId) { toast.error('Invoice ID missing'); return; }

        setLoading(true);
        try {
            const res = await fetchApi(`/einvoice/generate/${invoiceId}`, { method: 'POST', body: '{}' });
            toast.success(res.isDuplicate
                ? `IRN already existed — loaded successfully`
                : `E-Invoice (IRN) generated successfully!`
            );
            setApiResponse(res);
            setConfirmed(false);
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err.message || 'Failed to generate IRN');
        } finally {
            setLoading(false);
        }
    };

    // ── Cancel IRN ────────────────────────────────────────────────────────────
    const handleCancelIRN = async () => {
        if (!cancelForm.cancelRemark.trim()) { toast.warn('Cancel remark is required'); return; }
        const conf = await Swal.fire({
            title: 'Cancel E-Invoice / IRN?',
            html: `<b>IRN:</b> ${irn}<br/>This action cannot be undone.`,
            icon: 'warning', showCancelButton: true,
            confirmButtonColor: '#991b1b', confirmButtonText: 'Yes, Cancel IRN',
            cancelButtonText: 'No, Keep It'
        });
        if (!conf.isConfirmed) return;

        setLoading(true);
        try {
            const res = await fetchApi(`/einvoice/cancel/${invoiceId}`, {
                method: 'POST',
                body: JSON.stringify({ cancelReason: cancelForm.cancelReason, cancelRemark: cancelForm.cancelRemark })
            });
            toast.success('IRN cancelled successfully');
            setShowCancel(false);
            setApiResponse(res);
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err.message || 'IRN cancellation failed');
        } finally {
            setLoading(false);
        }
    };

    // ── Generate EWB via IRN ──────────────────────────────────────────────────
    const handleGenerateEWBByIRN = async () => {
        setLoading(true);
        try {
            const res = await fetchApi(`/einvoice/generate-ewb/${invoiceId}`, {
                method: 'POST',
                body: JSON.stringify(ewbForm)
            });
            toast.success(`E-Way Bill generated via IRN: ${res.ewbNo}`);
            setShowEWB(false);
            setApiResponse(res);
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err.message || 'Failed to generate EWB via IRN');
        } finally {
            setLoading(false);
        }
    };

    // ── Fetch IRN Details ─────────────────────────────────────────────────────
    const handleFetchIRNDetails = async () => {
        if (!irn) return;
        setLoading(true);
        try {
            const res = await fetchApi(`/einvoice/irn/${invoiceId}`);
            setApiResponse(res.data || res);
            setShowResponse(true);
            toast.success('IRN details fetched');
        } catch (err) {
            toast.error(err.message || 'Failed to fetch IRN details');
        } finally {
            setLoading(false);
        }
    };

    // ── Download E-Invoice PDF ────────────────────────────────────────────────
    const handleDownloadEIPDF = () => {
        if (!invoiceId) return;
        // Delegate to parent-provided PDF download function (same as regular PDF)
        if (onDownloadPDF) {
            onDownloadPDF();
        } else {
            toast.warn('PDF generation is not available.');
        }
    };

    // ═════════════════════════════════════════════════════════════════════════
    // Render
    // ═════════════════════════════════════════════════════════════════════════
    return (
        <div style={panelStyle}>

            {/* ── Header ───────────────────────────────────────── */}
            <div style={{ padding: '14px 16px 8px 16px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '17px', fontWeight: 700, color: '#4b5563', marginBottom: '10px' }}>
                    E-Invoice
                </div>

                {/* Status Badge */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: statusInfo.bg, color: statusInfo.color,
                    border: `1px solid ${statusInfo.border}`,
                    borderRadius: '20px', padding: '3px 10px', fontSize: '11px',
                    fontWeight: 700, marginBottom: '10px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: statusInfo.color, display: 'inline-block' }} />
                    {statusInfo.label}
                </div>

                {/* Info Rows */}
                <div style={{ fontSize: '12px', lineHeight: '1.9' }}>
                    <div style={infoRow}><span style={infoLabel}>Status:</span><span style={infoVal}>{statusInfo.label}</span></div>
                    {irn && <div style={infoRow}><span style={infoLabel}>IRN:</span><span style={{ ...infoVal, fontSize: '10px', wordBreak: 'break-all' }}>{irn}</span></div>}
                    {ackNo && <div style={infoRow}><span style={infoLabel}>Ack No:</span><span style={infoVal}>{ackNo}</span></div>}
                    {ackDate && <div style={infoRow}><span style={infoLabel}>Ack Date:</span><span style={infoVal}>{ackDate}</span></div>}
                    {einvoiceStatus === 'CANCELLED' && (
                        <>
                            <div style={infoRow}><span style={infoLabel}>Cancel Status:</span><span style={{ ...infoVal, color: '#991b1b' }}>Cancelled</span></div>
                            <div style={infoRow}><span style={infoLabel}>Cancel Date:</span><span style={infoVal}>{cancelDate}</span></div>
                            <div style={infoRow}><span style={infoLabel}>Message:</span><span style={infoVal}>{cancelRemark}</span></div>
                        </>
                    )}
                </div>
            </div>



            {/* ── Generate Form (shown when PENDING or CANCELLED) ── */}
            {(einvoiceStatus === 'PENDING') && (
                <div style={{ margin: '14px 14px 0 14px', padding: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fafafa' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '12px' }}>
                        Generate E-Invoice / IRN
                    </div>

                    {invoiceData?.ewb_status !== 'GENERATED' && !invoiceData?.e_way_bill_no ? (
                        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '7px', padding: '10px', color: '#991b1b', fontSize: '12px', fontWeight: 600, textAlign: 'center', lineHeight: '1.4' }}>
                            ⚠️ Please generate E-Way Bill first before generating E-Invoice (IRN).
                        </div>
                    ) : (
                        <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', marginBottom: '14px' }}>
                                <input
                                    type="checkbox"
                                    checked={confirmed}
                                    onChange={e => setConfirmed(e.target.checked)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#1e3a5f' }}
                                />
                                <span style={{ fontSize: '12px', color: '#111827', fontWeight: 600 }}>Confirm Generate E-Invoice / IRN</span>
                            </label>

                            <button
                                style={{ ...btnPrimary, background: confirmed ? '#1e3a5f' : '#9ca3af', cursor: confirmed ? 'pointer' : 'not-allowed' }}
                                onClick={handleGenerateIRN}
                                disabled={loading || !confirmed}
                            >
                                {loading ? 'Generating...' : 'Generate IRN'}
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* ── Actions when GENERATED ─────────────────────── */}
            {einvoiceStatus === 'GENERATED' && (
                <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>

                    {/* E-Invoice PDF */}
                    <button style={btnSecondary} onClick={handleDownloadEIPDF} disabled={loading}>
                        E-Invoice PDF
                    </button>

                    {/* Cancel IRN */}
                    {(() => {
                        let isCancelAllowed = false;
                        if (ackDate) {
                            const ackTime = new Date(ackDate.replace(' ', 'T')).getTime(); // Ensure valid parsing
                            const now = new Date().getTime();
                            const diffHours = (now - ackTime) / (1000 * 60 * 60);
                            isCancelAllowed = diffHours <= 24;
                        }

                        if (!isCancelAllowed && ackDate) {
                            return (
                                <div style={{ background: '#ffedd5', border: '1px solid #fed7aa', borderRadius: '7px', padding: '12px', marginTop: '6px', color: '#ea580c', fontSize: '12px', fontWeight: 500, lineHeight: '1.4' }}>
                                    E-Invoice cannot be cancelled because more than 24 hours have passed since E-Invoice generation.
                                </div>
                            );
                        }

                        return (
                            <>
                                <button style={btnDanger} onClick={() => setShowCancel(v => !v)}>
                                    Cancel IRN
                                </button>
                                {showCancel && (
                                    <div style={{ background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: '7px', padding: '10px', marginTop: '4px' }}>
                                        <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: 600, marginBottom: '8px' }}>
                                            ⚠️ IRN cancellation is only allowed within 24 hours of generation via NIC portal.
                                        </div>
                                        <div style={labelStyle}>Cancel Reason</div>
                                        <select style={selectStyle} value={cancelForm.cancelReason} onChange={e => setCancelForm(p => ({ ...p, cancelReason: e.target.value }))}>
                                            {CANCEL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                        </select>
                                        <div style={{ ...labelStyle, marginTop: '6px' }}>Remarks</div>
                                        <input style={inputStyle} value={cancelForm.cancelRemark}
                                            onChange={e => setCancelForm(p => ({ ...p, cancelRemark: e.target.value }))}
                                            placeholder="Enter cancellation remarks..." />
                                        <button style={{ ...btnDanger, marginTop: '8px' }} onClick={handleCancelIRN} disabled={loading}>
                                            {loading ? 'Cancelling...' : 'Confirm Cancel IRN'}
                                        </button>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
            )}

            {/* ── API Response Viewer ────────────────────────── */}
            {apiResponse && (
                <div style={sectionStyle}>
                    <button style={{ ...btnSecondary, marginTop: 0 }} onClick={() => setShowResponse(v => !v)}>
                        {showResponse ? 'Hide API Response' : 'View API Response'}
                    </button>
                    {showResponse && (
                        <pre style={{ marginTop: '8px', background: '#1e293b', color: '#e2e8f0', borderRadius: '6px', padding: '10px', fontSize: '10px', overflowX: 'auto', maxHeight: '200px', overflowY: 'auto' }}>
                            {JSON.stringify(apiResponse, null, 2)}
                        </pre>
                    )}
                </div>
            )}

        </div>
    );
}
